import { useState } from "react";
import { Zap } from "lucide-react";
import { getAccionDeHoy } from "../data/accionDeHoy";

// localStorage key holding a map: { [giro::reto]: variantIdx }.
// This is what makes the "recuerda combos" rule work — we always show a
// DIFFERENT variant than the one shown last time for the same giro+reto.
const LAST_KEY = "negoia:acciones:last";

// Module-level cache: survives React StrictMode's mount/unmount/remount cycle
// (which would otherwise cause a double-rotation), but is cleared explicitly
// by App.js on handleRestart via resetAccionSessionCache() below.
// Rebuilds from scratch on a full page reload.
const moduleCache = new Map();

export function resetAccionSessionCache() {
  moduleCache.clear();
}

function readLastMap() {
  try {
    const raw = localStorage.getItem(LAST_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeLastVariant(lookupKey, variantIdx) {
  try {
    const map = readLastMap();
    map[lookupKey] = variantIdx;
    localStorage.setItem(LAST_KEY, JSON.stringify(map));
  } catch {
    /* private mode / quota — harmless */
  }
}

// Extra card rendered at the end of Screen3Results (before "Generar de nuevo").
// Always visible — falls back to a giro-generic action when no exact reto match.
export default function AccionDeHoy({ businessType, reto }) {
  // useState with lazy initializer runs once per real mount. The moduleCache
  // guard ensures StrictMode's synthetic double-mount doesn't rotate twice.
  const [accion] = useState(() => {
    const cacheKey = `${businessType || "otro"}::${reto || "__generic__"}`;
    if (moduleCache.has(cacheKey)) return moduleCache.get(cacheKey);
    const lastMap = readLastMap();
    const chosen = getAccionDeHoy(businessType, reto, lastMap[cacheKey]);
    writeLastVariant(chosen.lookupKey, chosen.variantIdx);
    moduleCache.set(cacheKey, chosen);
    return chosen;
  });

  return (
    <div
      className="mt-4 rounded-2xl border-2 p-5"
      style={{ backgroundColor: "#FFF7ED", borderColor: "#F97316" }}
      data-testid="accion-de-hoy-card"
    >
      <div className="flex items-start gap-2 mb-3">
        <span
          className="flex items-center justify-center w-9 h-9 rounded-lg shrink-0"
          style={{ backgroundColor: "#F97316" }}
        >
          <Zap className="w-5 h-5 text-white" strokeWidth={2.5} />
        </span>
        <h3
          className="text-lg font-bold leading-snug pt-1"
          style={{ color: "#9A3412" }}
          data-testid="accion-de-hoy-title"
        >
          ⚡ Acción de hoy
        </h3>
      </div>

      <p
        className="text-sm text-slate-800 leading-relaxed whitespace-pre-line"
        data-testid="accion-de-hoy-text"
      >
        {accion.text}
      </p>

      <p
        className="mt-4 text-xs text-slate-500 leading-snug"
        data-testid="accion-de-hoy-source"
      >
        {accion.source}
      </p>
    </div>
  );
}
